import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Panel, Format, If, Text, Unless } from '../../..'
import { LockableInlineCurrencyText } from '../../CopyHelpers'

import styles from './StatusExplainer.module.scss'

const propTypes = {
  interestStatus: PropTypes.string.isRequired,
  interestTotal: PropTypes.number.isRequired,
  interestDiff: PropTypes.number,
  loans: PropTypes.arrayOf(
    PropTypes.shape({
      termYears: PropTypes.number, // one or the other
      termYearsEstimated: PropTypes.number,
    }).isRequired
  ).isRequired,
  locked: PropTypes.bool,
}

const StatusExplainer = ({
  interestStatus,
  interestTotal,
  interestDiff,
  loans,
  locked,
}) => {
  const loanDescription = (
    <>
      <If condition={loans.length > 1}>
        <FormattedMessage
          id='Homeowner.interestCard.loanDescriptor.multiple'
          defaultMessage='{quantity} loans'
          values={{ quantity: Format.number(loans.length) }}
        />
      </If>
      <Unless condition={loans.length > 1}>
        <FormattedMessage
          id='Homeowner.interestCard.loanDescriptor'
          defaultMessage='{term} year loan'
          values={{ term: loans[0].termYears || loans[0].termYearsEstimated }}
        />
      </Unless>
    </>
  )

  return (
    <>
      <Panel.Heading className={styles.statusHeadline}>
        <If condition={interestStatus === 'good'}>
          <FormattedMessage
            id='Homeowner.interestCard.totalInterestHeadline.good'
            defaultMessage='Your interest amount is low. Good work.'
          />
        </If>
        <If condition={interestStatus !== 'good'}>
          <FormattedMessage
            id='Homeowner.interestCard.totalInterestHeadline'
            defaultMessage='Interest Adds Up'
          />
        </If>
      </Panel.Heading>
      <Text
        className={styles.statusExplainer}
        align='center'
        size='s'
        variant='neutralCool'
        variantCount='900'
      >
        <Unless condition={interestDiff > 0}>
          <FormattedMessage
            id='Homeowner.interest.statusExplainer'
            defaultMessage="Over your {loanDescription} you'll pay {amount} in interest."
            values={{
              loanDescription,
              amount: LockableInlineCurrencyText({
                currency: interestTotal,
                variant: 'danger',
                locked,
              }),
            }}
          />{' '}
          <If condition={interestStatus === 'good'}>
            <FormattedMessage
              id='Homeowner.interest.status.good'
              defaultMessage='Here are some tips to save more of that interest.'
            />
          </If>
          <Unless condition={interestStatus === 'good'}>
            <FormattedMessage
              id='Homeowner.interest.status'
              defaultMessage='Here are some tips to get ahead and save some of that interest.'
            />
          </Unless>
        </Unless>
        <If condition={interestDiff > 0}>
          <FormattedMessage
            id='Homeowner.interest.statusExplainer.withPayments'
            defaultMessage="Over your {loanDescription} you were due to pay {interestAmount} in interest but since you're paying extra, you're on track to save {saveAmount} of that interest"
            values={{
              loanDescription,
              interestAmount: LockableInlineCurrencyText({
                currency: interestTotal + interestDiff,
                variant: 'danger',
                locked,
              }),
              saveAmount: LockableInlineCurrencyText({
                currency: interestDiff,
                variant: 'success',
                locked,
              }),
            }}
          />
        </If>
      </Text>
    </>
  )
}

StatusExplainer.propTypes = propTypes

export default StatusExplainer
